import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { authAPI } from '../../services/api';
import Seo from '../../components/Seo';

const S = {
  page: { padding: 24, background: 'var(--cream)', minHeight: '100vh' },
  title: { fontFamily: 'var(--font-serif)', fontSize: 28, fontWeight: 700, color: 'var(--char)', marginBottom: 4 },
  subtitle: { fontSize: 13, color: 'var(--muted)', marginBottom: 24 },
  grid: { display: 'grid', gridTemplateColumns: '1fr', gap: 24, maxWidth: 800 },
  card: { background: '#fff', border: '1px solid var(--border)', borderRadius: 16, padding: 28, boxShadow: '0 4px 12px rgba(0,0,0,0.03)' },
  sectionTitle: { fontSize: 16, fontWeight: 700, color: 'var(--char)', marginBottom: 18, borderBottom: '1px solid var(--border)', paddingBottom: 10, display: 'flex', alignItems: 'center', gap: 8 },
  infoGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 20 },
  infoGroup: { display: 'flex', flexDirection: 'column', gap: 4 },
  label: { fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.5 },
  val: { fontSize: 15, fontWeight: 600, color: 'var(--char)' },
  row: { marginBottom: 20 },
  inputLabel: { fontSize: 12, fontWeight: 700, color: 'var(--char)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8, display: 'block' },
  input: { width: '100%', padding: '12px 14px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 14, outline: 'none', transition: 'border-color 0.2s', fontFamily: 'var(--font-sans)' },
  btn: { background: '#4C1D95', color: '#fff', border: 'none', padding: '12px 24px', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer', transition: 'opacity 0.2s, transform 0.1s' },
  alert: { padding: '14px 18px', borderRadius: 10, marginBottom: 20, fontSize: 13, display: 'flex', alignItems: 'center', gap: 10, fontWeight: 500 },
  avatarContainer: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 },
  avatar: { width: 64, height: 64, borderRadius: '50%', background: '#F3E8FF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, fontWeight: 700, color: '#4C1D95', border: '3px solid #fff', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }
};

export default function AdminProfile() {
  const { user } = useAuth();
  const [form, setForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [fieldErrors, setFieldErrors] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setToast(null);
    setFieldErrors({ currentPassword: '', newPassword: '', confirmPassword: '' });

    let hasErrors = false;
    const newFieldErrors = { currentPassword: '', newPassword: '', confirmPassword: '' };

    if (!form.currentPassword) {
      newFieldErrors.currentPassword = 'Current password is required.';
      hasErrors = true;
    }

    if (!form.newPassword) {
      newFieldErrors.newPassword = 'New password is required.';
      hasErrors = true;
    } else if (form.newPassword.length < 6) {
      newFieldErrors.newPassword = 'New password must be at least 6 characters long.';
      hasErrors = true;
    }

    if (!form.confirmPassword) {
      newFieldErrors.confirmPassword = 'Confirm new password is required.';
      hasErrors = true;
    } else if (form.newPassword !== form.confirmPassword) {
      newFieldErrors.confirmPassword = 'Confirm password does not match new password.';
      hasErrors = true;
    }

    if (hasErrors) {
      setFieldErrors(newFieldErrors);
      return;
    }

    setSaving(true);
    try {
      const res = await authAPI.changePassword({
        currentPassword: form.currentPassword,
        newPassword: form.newPassword,
      });

      if (res.data.success) {
        setToast({ type: 'success', message: 'Password updated successfully!' });
        setForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        setToast({ type: 'error', message: res.data.message || 'Failed to update password.' });
      }
    } catch (err) {
      const errMsg = err.response?.data?.message || 'Failed to update password.';
      if (err.response?.status === 401 || errMsg.toLowerCase().includes('current password')) {
        setFieldErrors(prev => ({ ...prev, currentPassword: errMsg }));
      } else {
        setToast({ type: 'error', message: errMsg });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={S.page}>
      <Seo title="Admin Profile - Kanjusee" />
      <div style={S.title}>Admin Profile</div>
      <div style={S.subtitle}>View your account details and manage your security preferences.</div>

      <div style={S.grid}>
        {/* Profile Card */}
        <div style={S.card}>
          <div style={S.sectionTitle}>👤 Profile Information</div>
          <div style={S.avatarContainer}>
            <div style={S.avatar}>
              {user?.fullName?.charAt(0)?.toUpperCase() || 'A'}
            </div>
            <div>
              <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--char)' }}>{user?.fullName}</div>
              <div style={{ fontSize: 12, color: 'var(--muted)', textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.5 }}>{user?.role}</div>
            </div>
          </div>

          <div style={S.infoGrid}>
            <div style={S.infoGroup}>
              <span style={S.label}>Full Name</span>
              <span style={S.val}>{user?.fullName || 'N/A'}</span>
            </div>
            <div style={S.infoGroup}>
              <span style={S.label}>Email Address</span>
              <span style={S.val}>{user?.email || 'N/A'}</span>
            </div>
            <div style={S.infoGroup}>
              <span style={S.label}>Mobile Number</span>
              <span style={S.val}>{user?.mobileNumber || 'N/A'}</span>
            </div>
          </div>
        </div>

        {/* Change Password Card */}
        <div style={S.card}>
          <div style={S.sectionTitle}>🔒 Change Password</div>
          {toast && (
            <div style={{
              ...S.alert,
              background: toast.type === 'success' ? '#ECFDF5' : '#FEF2F2',
              color: toast.type === 'success' ? '#065F46' : '#991B1B',
              border: `1.5px solid ${toast.type === 'success' ? '#A7F3D0' : '#FCA5A5'}`,
              marginBottom: 20
            }}>
              <span>{toast.type === 'success' ? '✅' : '⚠️'}</span>
              <span>{toast.message}</span>
            </div>
          )}
          <form onSubmit={handlePasswordChange}>
            <div style={S.row}>
              <label style={S.inputLabel}>Current Password</label>
              <input
                type="password"
                style={{
                  ...S.input,
                  borderColor: fieldErrors.currentPassword ? '#DC2626' : 'var(--border)',
                  boxShadow: fieldErrors.currentPassword ? '0 0 0 1px rgba(220, 38, 38, 0.1)' : 'none'
                }}
                value={form.currentPassword}
                onChange={e => setForm({ ...form, currentPassword: e.target.value })}
                placeholder="Enter current password"
              />
              {fieldErrors.currentPassword && (
                <div style={{ color: '#DC2626', fontSize: 12, marginTop: 5, fontWeight: 600 }}>
                  ⚠️ {fieldErrors.currentPassword}
                </div>
              )}
            </div>
            <div style={S.row}>
              <label style={S.inputLabel}>New Password</label>
              <input
                type="password"
                style={{
                  ...S.input,
                  borderColor: fieldErrors.newPassword ? '#DC2626' : 'var(--border)',
                  boxShadow: fieldErrors.newPassword ? '0 0 0 1px rgba(220, 38, 38, 0.1)' : 'none'
                }}
                value={form.newPassword}
                onChange={e => setForm({ ...form, newPassword: e.target.value })}
                placeholder="Enter new password (min. 6 characters)"
              />
              {fieldErrors.newPassword && (
                <div style={{ color: '#DC2626', fontSize: 12, marginTop: 5, fontWeight: 600 }}>
                  ⚠️ {fieldErrors.newPassword}
                </div>
              )}
            </div>
            <div style={S.row}>
              <label style={S.inputLabel}>Confirm New Password</label>
              <input
                type="password"
                style={{
                  ...S.input,
                  borderColor: fieldErrors.confirmPassword ? '#DC2626' : 'var(--border)',
                  boxShadow: fieldErrors.confirmPassword ? '0 0 0 1px rgba(220, 38, 38, 0.1)' : 'none'
                }}
                value={form.confirmPassword}
                onChange={e => setForm({ ...form, confirmPassword: e.target.value })}
                placeholder="Confirm new password"
              />
              {fieldErrors.confirmPassword && (
                <div style={{ color: '#DC2626', fontSize: 12, marginTop: 5, fontWeight: 600 }}>
                  ⚠️ {fieldErrors.confirmPassword}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
              <button
                type="submit"
                disabled={saving}
                style={{
                  ...S.btn,
                  opacity: saving ? 0.7 : 1,
                  cursor: saving ? 'not-allowed' : 'pointer'
                }}
              >
                {saving ? 'Updating...' : 'Update Password'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
